// src/components/AdsSection.jsx
import { useAdsForUser } from '../../hooks/useAds'; // useAdsForUserをインポート
import { Ad } from '../../types/index'; // Profile型をインポート
import { logger } from '../../utils/logger';

/**
 * 広告セクション
 * useAdsForUserフックで広告を表示
 */
interface AdsSectionProps {
  userId?: string; // userId を Props として受け取ることを明示
}

const AdsSection: React.FC<AdsSectionProps> = ({ userId }) => {
  // useAdsForUserフックで広告データを取得
  const { ads, isLoading: adsLoading, error: adsError } = useAdsForUser(userId);

  if (adsError) {
    logger.error('AdsSection: 広告データのロード中にエラーが発生しました。', adsError);
    return <div className="text-red-500 text-center">広告の表示に失敗しました。</div>;
  }

  // ロード中
  if (adsLoading) {
    return null;
  }

  // 広告がない場合は何も表示しない
  if (!ads || ads.length === 0) {
    logger.log('AdsSection: 表示可能な広告なし', {
      adsLoading,
      adsLength: ads ? ads.length : 'null',
    });
    return null;
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-700 mt-8 mb-4 text-center">今週のおすすめ商品</h2>
      <div className={`grid grid-cols-1 ${ads.length > 1 ? 'sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
        {ads.map((ad: Ad) => (
          <div key={ad.id} className="bg-yellow-50 rounded-2xl shadow-lg group hover:-translate-y-2 transition-all">
            {/* aタグをflex-colにし、内部要素を縦に並べる */}
            <a href={ad.url} target="_blank" rel="noopener noreferrer" className="block p-6 h-full flex flex-col">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{ad.title}</h3>

              {/* image_url が存在する場合のみ画像を表示し、タイトルの下に配置 */}
              {ad.image_url && (
                <img
                  src={ad.image_url}
                  alt={ad.title}
                  className="w-full h-32 object-cover rounded-md mb-4" // サイズ指定とマージン
                  // 画像が読み込めなかった場合のフォールバック（プレースホルダー画像）
                  onError={(e) => {
                    e.currentTarget.onerror = null; // エラーの無限ループを防ぐ
                    e.currentTarget.src = `https://placehold.co/400x128/cccccc/ffffff?text=No Image`;
                  }}
                />
              )}

              {/* flex-grow を使って、カテゴリと作成者名を下部に押しやるためのスペーサー */}
              <div className="flex-grow"></div>

              {/* カテゴリと作成者名を並べて表示するコンテナ */}
              <div className="flex items-center justify-between mt-auto"> {/* mt-auto で下部に固定 */}
                <p className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full font-medium">
                  {ad.category}
                </p>
                {ad.authorNickname && <p className="text-gray-500 text-xs">作成者: {ad.authorNickname}</p>}
              </div>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdsSection;
